import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { usePump } from "@/context/PumpContext";
import { useProducts } from "@/lib/products";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Save, Fuel, Clock, MessageCircle, Send, X, Plus, AlertTriangle, Building2 } from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

export default function AdminSettings() {
  const { pump } = usePump();
  const products = useProducts();
  const [prices, setPrices] = useState({});
  const [shifts, setShifts] = useState({
    shift1_start: "07:00", shift1_end: "14:30",
    shift2_start: "14:30", shift2_end: "22:00",
    shift3_start: "22:00", shift3_end: "07:00",
    business_day_start: "07:00",
  });
  const [waNumbers, setWaNumbers] = useState([]);
  const [waInput, setWaInput] = useState("");
  const [profile, setProfile] = useState({ name: "", gstin: "", address: "", phone: "", late_grace_minutes: 15 });

  useEffect(() => {
    api.get("/settings/fuel-prices").then((r) => setPrices(r.data));
    api.get("/settings/shifts").then((r) => setShifts(r.data));
    api.get("/settings/whatsapp").then((r) => setWaNumbers(r.data.numbers || [])).catch(() => {});
    api.get("/settings/pump-profile").then((r) => setProfile((p) => ({ ...p, ...Object.fromEntries(Object.entries(r.data).filter(([, v]) => v != null)) }))).catch(() => {});
  }, []);

  const saveProfile = async () => {
    try {
      const { data } = await api.put("/settings/pump-profile", { ...profile, late_grace_minutes: Number(profile.late_grace_minutes) });
      setProfile((p) => ({ ...p, ...data })); toast.success("Pump details saved");
    } catch (e) { toast.error(formatError(e)); }
  };

  const savePrices = async () => {
    try {
      await api.put("/settings/fuel-prices", { prices: Object.fromEntries(products.map((p) => [p.key, Number(prices[p.key] || 0)])) });
      toast.success("Fuel prices updated");
    } catch (e) { toast.error(formatError(e)); }
  };

  const saveShifts = async () => {
    try {
      await api.put("/settings/shifts", shifts);
      toast.success("Shift timings saved");
    } catch (e) { toast.error(formatError(e)); }
  };

  const addWa = () => {
    if (!waInput.trim()) return;
    setWaNumbers([...waNumbers, waInput.trim()]);
    setWaInput("");
  };

  const saveWa = async () => {
    try {
      const { data } = await api.put("/settings/whatsapp", { numbers: waNumbers });
      setWaNumbers(data.numbers);
      toast.success("WhatsApp recipients saved");
    } catch (e) { toast.error(formatError(e)); }
  };

  const sendEodNow = async () => {
    try {
      await api.post("/admin/send-eod-now");
      toast.success("End-of-day report queued for delivery");
    } catch (e) { toast.error(formatError(e)); }
  };

  const [resetOpen, setResetOpen] = useState(false);
  const [resetPhrase, setResetPhrase] = useState("");
  const [resetting, setResetting] = useState(false);
  const doReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-shift-data", { confirm: "DELETE ALL SHIFTS" });
      toast.success(`Deleted ${data.deleted.shifts} shifts, ${data.deleted.notifications} notifications, ${data.deleted.whatsapp_log} whatsapp logs`);
      setResetOpen(false);
      setResetPhrase("");
    } catch (e) { toast.error(formatError(e)); }
    finally { setResetting(false); }
  };

  return (
    <AdminLayout>
      <h1 className="text-3xl font-display font-bold mb-1">Settings</h1>
      <p className="text-sm text-slate-500 mb-6">Configure fuel prices and shift timings.</p>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-5 rounded-2xl border-slate-200">
          <div className="flex items-center gap-2 mb-1">
            <Fuel className="w-4 h-4 text-[color:var(--bpcl-blue)]" />
            <div className="font-display font-bold">Fuel Prices</div>
          </div>
          <p className="text-xs text-slate-500 mb-4">Applied to new shifts from this moment onward.</p>
          <div className="space-y-3">
            {products.map((p) => { const k = p.key; return (
              <div key={k}>
                <Label className="text-xs uppercase tracking-wide text-slate-500">{p.name} · ₹ per {p.unit}</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                  <Input
                    data-testid={`price-${k}`}
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    value={prices[k] ?? ""}
                    onChange={(e) => setPrices({ ...prices, [k]: e.target.value })}
                    className="h-12 rounded-xl pl-7 font-mono-num text-lg"
                  />
                </div>
              </div>
            ); })}
          </div>
          <Button
            onClick={savePrices}
            data-testid="save-prices-btn"
            className="mt-4 w-full h-12 rounded-xl bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)]"
          >
            <Save className="w-4 h-4 mr-2" /> Save prices
          </Button>
        </Card>

        <Card className="p-5 rounded-2xl border-slate-200">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-[color:var(--bpcl-blue)]" />
            <div className="font-display font-bold">Shift Timings</div>
          </div>
          <p className="text-xs text-slate-500 mb-4">Business day starts at 7:00 AM by default.</p>
          <div className="space-y-3">
            <TwoInput label="Shift 1" from={shifts.shift1_start} to={shifts.shift1_end}
              onFrom={(v) => setShifts({ ...shifts, shift1_start: v })}
              onTo={(v) => setShifts({ ...shifts, shift1_end: v })} k="1" />
            <TwoInput label="Shift 2" from={shifts.shift2_start} to={shifts.shift2_end}
              onFrom={(v) => setShifts({ ...shifts, shift2_start: v })}
              onTo={(v) => setShifts({ ...shifts, shift2_end: v })} k="2" />
            {(pump?.shift_count || 3) >= 3 && <TwoInput label="Shift 3" from={shifts.shift3_start} to={shifts.shift3_end}
              onFrom={(v) => setShifts({ ...shifts, shift3_start: v })}
              onTo={(v) => setShifts({ ...shifts, shift3_end: v })} k="3" />}
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Business day start</Label>
              <Input
                data-testid="business-day-start"
                type="time"
                value={shifts.business_day_start}
                onChange={(e) => setShifts({ ...shifts, business_day_start: e.target.value })}
                className="h-12 rounded-xl mt-1"
              />
            </div>
          </div>
          <Button
            onClick={saveShifts}
            data-testid="save-shifts-btn"
            className="mt-4 w-full h-12 rounded-xl bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)]"
          >
            <Save className="w-4 h-4 mr-2" /> Save timings
          </Button>
        </Card>
      </div>

      <Card className="p-5 rounded-2xl border-slate-200 mt-5" data-testid="pump-profile-card">
        <div className="flex items-center gap-2 mb-1"><Building2 className="w-4 h-4 text-[color:var(--bpcl-blue)]" /><div className="font-display font-bold">Pump details & attendance</div></div>
        <p className="text-xs text-slate-500 mb-4">GSTIN, address and phone print on every credit invoice. Grace minutes decide when a login selfie is marked LATE (after the nearest shift start).</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label className="text-xs">Outlet name</Label><Input value={profile.name || ""} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="h-11 rounded-xl mt-1" data-testid="profile-name" /></div>
          <div><Label className="text-xs">GSTIN</Label><Input value={profile.gstin || ""} onChange={(e) => setProfile({ ...profile, gstin: e.target.value.toUpperCase() })} placeholder="03ABCDE1234F1Z5" className="h-11 rounded-xl mt-1 font-mono-num uppercase" data-testid="profile-gstin" /></div>
          <div className="md:col-span-2"><Label className="text-xs">Address</Label><Input value={profile.address || ""} onChange={(e) => setProfile({ ...profile, address: e.target.value })} placeholder="Full address with PIN" className="h-11 rounded-xl mt-1" data-testid="profile-address" /></div>
          <div><Label className="text-xs">Phone</Label><Input value={profile.phone || ""} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="h-11 rounded-xl mt-1 font-mono-num" data-testid="profile-phone" /></div>
          <div><Label className="text-xs">Late after (minutes past shift start)</Label><Input type="number" min="0" max="240" value={profile.late_grace_minutes ?? 15} onChange={(e) => setProfile({ ...profile, late_grace_minutes: e.target.value })} className="h-11 rounded-xl mt-1 font-mono-num" data-testid="profile-grace" /></div>
        </div>
        <Button onClick={saveProfile} className="mt-4 rounded-xl bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)]" data-testid="profile-save"><Save className="w-4 h-4 mr-2" /> Save pump details</Button>
      </Card>

      <Card className="p-5 rounded-2xl border-slate-200 mt-5">
        <div className="flex items-center gap-2 mb-1">
          <MessageCircle className="w-4 h-4 text-emerald-600" />
          <div className="font-display font-bold">WhatsApp reports</div>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          These numbers receive an automatic message after every shift completion and a summary at end-of-day (7:00 AM IST).
        </p>
        <div className="space-y-2 mb-3">
          {waNumbers.length === 0 && (
            <div className="text-sm text-slate-400 italic">No recipients yet. Add at least one number.</div>
          )}
          {waNumbers.map((n, i) => (
            <div key={i} className="flex items-center gap-2 rounded-xl border border-slate-200 p-2.5" data-testid={`wa-num-${i}`}>
              <Badge className="rounded-full bg-emerald-50 text-emerald-700 border-emerald-200">WhatsApp</Badge>
              <div className="font-mono-num text-sm flex-1">{n}</div>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setWaNumbers(waNumbers.filter((_, idx) => idx !== i))}
                data-testid={`wa-remove-${i}`}
              >
                <X className="w-4 h-4 text-slate-500" />
              </Button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            data-testid="wa-input"
            type="tel"
            placeholder="+91XXXXXXXXXX"
            value={waInput}
            onChange={(e) => setWaInput(e.target.value)}
            className="h-11 rounded-xl font-mono-num"
          />
          <Button onClick={addWa} variant="outline" className="h-11 rounded-xl" data-testid="wa-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
        <div className="flex gap-2 mt-4">
          <Button
            onClick={saveWa}
            data-testid="save-wa-btn"
            className="rounded-xl bg-emerald-600 hover:bg-emerald-700 flex-1"
          >
            <Save className="w-4 h-4 mr-2" /> Save recipients
          </Button>
          <Button
            onClick={sendEodNow}
            variant="outline"
            data-testid="send-eod-now-btn"
            className="rounded-xl flex-1"
          >
            <Send className="w-4 h-4 mr-2" /> Send today's report now
          </Button>
        </div>
      </Card>

      {/* Danger Zone */}
      <Card className="p-5 rounded-2xl border-red-300 mt-5 bg-red-50/40" data-testid="danger-zone">
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle className="w-4 h-4 text-red-600" />
          <div className="font-display font-bold text-red-700">Danger Zone</div>
        </div>
        <p className="text-xs text-slate-600 mb-4">
          Wipe all shift records, notifications and WhatsApp logs. Employees and settings stay intact.
          There is no undo.
        </p>
        <Button
          variant="outline"
          onClick={() => setResetOpen(true)}
          data-testid="open-reset-btn"
          className="rounded-xl border-red-400 text-red-700 hover:bg-red-100 hover:text-red-800"
        >
          <AlertTriangle className="w-4 h-4 mr-2" /> Reset all shift data
        </Button>
      </Card>

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700">Reset all shift data?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes every shift, notification and WhatsApp log record.
              Type <b>DELETE ALL SHIFTS</b> below to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            data-testid="reset-confirm-input"
            value={resetPhrase}
            onChange={(e) => setResetPhrase(e.target.value)}
            placeholder="DELETE ALL SHIFTS"
            className="rounded-xl"
          />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-cancel" onClick={() => setResetPhrase("")}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doReset(); }}
              disabled={resetPhrase !== "DELETE ALL SHIFTS" || resetting}
              data-testid="reset-confirm-btn"
              className="bg-red-600 hover:bg-red-700"
            >
              {resetting ? "Deleting…" : "Delete everything"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}

function TwoInput({ label, from, to, onFrom, onTo, k }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-slate-500">{label}</Label>
      <div className="grid grid-cols-2 gap-2 mt-1">
        <Input data-testid={`shift${k}-start`} type="time" value={from} onChange={(e) => onFrom(e.target.value)} className="h-12 rounded-xl" />
        <Input data-testid={`shift${k}-end`} type="time" value={to} onChange={(e) => onTo(e.target.value)} className="h-12 rounded-xl" />
      </div>
    </div>
  );
}
